import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Language, LanguageContextType } from '../i18n/types';
import { supabase } from '../lib/supabase';
import { useUserCompany } from '../hooks/useUserCompany';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const { company } = useUserCompany();

  // Get initial language from localStorage or fallback to 'en'
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('pulse-language');
    return (stored as Language) || 'en';
  });

  // Sync i18next language on mount and changes
  useEffect(() => {
    // Change i18next language
    i18n.changeLanguage(language);

    // Persist to localStorage
    localStorage.setItem('pulse-language', language);

    // Update HTML lang attribute for SEO and accessibility
    document.documentElement.lang = language;
  }, [language, i18n]);

  // Sync language preference to database
  const syncLanguageToDatabase = useCallback(async () => {
    if (!company?.id) return;

    try {
      const { error } = await supabase
        .from('companies')
        .update({
          financial_profile: {
            ...(company.financial_profile || {}),
            preferred_language: language
          }
        })
        .eq('id', company.id);

      if (error) {
        // Only log errors in development to avoid sensitive data in production console
        if (import.meta.env.DEV) {
          console.error('Failed to sync language to database:', error);
        }
      } else {
        // Only log in development to avoid sensitive data in production console
        if (import.meta.env.DEV) {
          console.log(`✅ Language synced to database: ${language}`);
        }
      }
    } catch (err) {
      // Only log errors in development to avoid sensitive data in production console
      if (import.meta.env.DEV) {
        console.error('Error syncing language:', err);
      }
    }
  }, [language, company]);

  useEffect(() => {
    syncLanguageToDatabase();
  }, [syncLanguageToDatabase]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Allow exporting hook from component file for convenience
// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
